import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';

const GoalContext = createContext();

export const useGoals = () => {
  const context = useContext(GoalContext);
  if (!context) {
    throw new Error('useGoals must be used within a GoalProvider');
  }
  return context;
};

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const GoalProvider = ({ children }) => {
  const { user } = useAuth();
  const [goals, setGoals] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [analytics, setAnalytics] = useState(null);

  // Fetch goals
  const fetchGoals = async (filters = {}) => {
    try {
      setLoading(true);
      setError(null);
      
      const params = new URLSearchParams();
      Object.keys(filters).forEach(key => {
        if (filters[key]) {
          params.append(key, filters[key]);
        }
      });
      
      const response = await axios.get(`${API}/goals?${params.toString()}`);
      setGoals(response.data);
      return { success: true, data: response.data };
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Failed to fetch goals';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  };

  // Fetch single goal
  const fetchGoal = async (goalId) => {
    try {
      setError(null);
      const response = await axios.get(`${API}/goals/${goalId}`);
      return { success: true, data: response.data };
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Failed to fetch goal';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  // Create goal
  const createGoal = async (goalData) => {
    try {
      setError(null);
      const response = await axios.post(`${API}/goals`, goalData);
      
      // Refresh goals list
      await fetchGoals();
      
      return { success: true, data: response.data };
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Failed to create goal';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  // Update goal
  const updateGoal = async (goalId, updateData) => {
    try {
      setError(null);
      const response = await axios.put(`${API}/goals/${goalId}`, updateData);
      
      // Update goal in local state
      setGoals(prevGoals => 
        prevGoals.map(goal => 
          goal.id === goalId ? response.data : goal
        )
      );
      
      return { success: true, data: response.data };
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Failed to update goal';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  // Update goal progress
  const updateGoalProgress = async (goalId, progressData) => {
    try {
      setError(null);
      await axios.post(`${API}/goals/${goalId}/progress`, progressData);
      
      // Refresh goals to get updated progress
      await fetchGoals();
      
      return { success: true };
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Failed to update goal progress';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  // Delete goal
  const deleteGoal = async (goalId) => {
    try {
      setError(null);
      await axios.delete(`${API}/goals/${goalId}`);
      
      // Remove goal from local state
      setGoals(prevGoals => prevGoals.filter(goal => goal.id !== goalId));
      
      return { success: true };
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Failed to delete goal';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  // Fetch analytics
  const fetchAnalytics = async () => {
    try {
      setError(null);
      const response = await axios.get(`${API}/goals/analytics/summary`);
      setAnalytics(response.data);
      return { success: true, data: response.data };
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Failed to fetch analytics';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  // Helper functions
  const getGoalsByStatus = (status) => {
    return goals.filter(goal => goal.status === status);
  };

  const getGoalsByCategory = (category) => {
    return goals.filter(goal => goal.category === category);
  };

  const getMyGoals = () => {
    return goals.filter(goal => 
      goal.assigned_to.includes(user?.id) || goal.created_by === user?.id
    );
  };

  // Load goals on mount
  useEffect(() => {
    if (user) {
      fetchGoals();
      fetchAnalytics();
    }
  }, [user]);

  const value = {
    goals,
    loading,
    error,
    analytics,
    fetchGoals,
    fetchGoal,
    createGoal,
    updateGoal,
    updateGoalProgress,
    deleteGoal,
    fetchAnalytics,
    getGoalsByStatus,
    getGoalsByCategory,
    getMyGoals
  };

  return (
    <GoalContext.Provider value={value}>
      {children}
    </GoalContext.Provider>
  );
};